import PropTypes from 'prop-types';
import React from 'react';
import Icon from '@loggi/ui/components/icon';
import View from '@loggi/ui/components/view';
import colors from '@loggi/ui/components/utils/colors';
import CheckboxShape from './components/checkbox-shape.js';
import classNames from 'classnames';
import styles from './index.scss';

const SPACE_KEY = 'Space';

class CheckboxButton extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.hasGroup = Boolean(context.selectedValue);

    this._handleOnChange = this._handleOnChange.bind(this);
    this._handleChecked = this._handleChecked.bind(this);
    this._getIndex = this._getIndex.bind(this);
    this._isChecked = this._isChecked.bind(this);
    this._onKeyPress = this._onKeyPress.bind(this);

    this.state = {
      checked: this.hasGroup ? this._isChecked() : null,
      labelFocused: false
    };

    if (!CheckboxButton.count) {
      CheckboxButton.count = 0;
    }
    CheckboxButton.count++;
    this._id = `checkbox-${CheckboxButton.count}`;
  }

  componentDidMount() {
    window.addEventListener('keypress', this._onKeyPress);
  }

  componentWillUnmount() {
    window.removeEventListener('keypress', this._onKeyPress);
  }

  _handleStateLabelFocused(labelFocused) {
    this.setState({ labelFocused });
  }

  _onKeyPress(e) {
    const { labelFocused } = this.state;
    const isSpace = e.code === SPACE_KEY;

    if (isSpace && labelFocused) {
      this._handleOnChange();
    }
  }

  getInput() {
    return this.refs.input;
  }

  _handleOnChange() {
    if (this.hasGroup) {
      const { onChange, selectedValue } = this.context;

      if (this.state.checked) {
        selectedValue.splice(this._getIndex(), 1);
      } else {
        selectedValue.push(this.props.value);
      }

      this._handleChecked();
      onChange(selectedValue);
    }

    if (!this.hasGroup && this.props.onChange) {
      this.props.onChange();
    }
  }

  _handleChecked() {
    const { checked } = this.state;
    this.setState({ checked: !checked });
  }

  _getIndex() {
    return this.context.selectedValue.indexOf(this.props.value);
  }

  _isChecked() {
    const index = this._getIndex();
    return this.context.selectedValue[index] === this.props.value;
  }

  render() {
    const { name } = this.context;
    const { checked } = this.state;
    const {
      type,
      className,
      color,
      disabled,
      checked: propsChecked
    } = this.props;
    const { label, tabIndex } = this.props;

    const isChecked = this.hasGroup ? checked : propsChecked;

    const propsInput = {
      name,
      type,
      defaultChecked: isChecked,
      onChange: disabled ? null : this._handleOnChange,
      style: { display: 'none' }
    };

    const classesCheckboxButton = classNames(
      className,
      styles.checkboxButton,
      { [styles.checked]: isChecked },
      { [styles.disabled]: disabled }
    );

    return (
      <View
        inline
        tabIndex={tabIndex}
        className={classesCheckboxButton}
        onFocus={this._handleStateLabelFocused.bind(this, true)}
        onBlur={this._handleStateLabelFocused.bind(this, false)}
      >
        <input ref="input" id={this._id} {...propsInput} />
        <label htmlFor={this._id} className={styles.label}>
          <View inline className={styles.wrapRound}>
            <CheckboxShape
              checked={isChecked}
              disabled={disabled}
              color={color}
            />
            <Icon className={styles.icon} type="check" />
          </View>
          {label && (
            <View
              inline
              className={styles.labelText}
              style={{
                color: !disabled
                  ? color === colors.type.ACTIVE
                    ? null
                    : colors.hex[color]
                  : colors.hex.disable
              }}
            >
              {label}
            </View>
          )}
        </label>
      </View>
    );
  }
}

CheckboxButton.displayName = 'CheckboxButton';

CheckboxButton.propTypes = {
  tabIndex: PropTypes.any,
  labelChangeColor: PropTypes.bool,
  color: PropTypes.oneOf([
    colors.type.ACTIVE,
    colors.type.INFO,
    colors.type.SUCCESS,
    colors.type.DANGER,
    colors.type.WARNING
  ]),
  type: PropTypes.string,
  checked: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool
};

CheckboxButton.defaultProps = {
  checked: false,
  disabled: false,
  type: 'checkbox',
  color: 'active',
  labelChangeColor: false
};

CheckboxButton.contextTypes = {
  name: PropTypes.string,
  selectedValue: PropTypes.array,
  onChange: PropTypes.func
};

export default CheckboxButton;
